import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { differenceInMonths } from "date-fns";
import { Grid, MenuItem, Typography } from "@mui/material";
import { AccountSettings } from "@akj-dev/affinity-platform";
import { AddressFields } from "../AddressFields";
import { IndexedCardInfo } from "../../../../atoms/indexed-card-info";
import { RHFSelectField } from "../../../../atoms/form-fields/RHFSelectField";
import { Alert } from "../../../../Alert/Alert";
import { DatePicker } from "../../../../DatePicker";

import { RHFSwitchField } from "../../../../atoms/form-fields/RHFSwitchField";
import { FetchWLR3AddressSearch } from "../../../AddressPicker/types";
import { useIsVfDirectConsumerAccount } from "../../hooks/useIsVfDirectConsumerAccount";
import { StatusChip } from "../../../../StatusChip/StatusChip";

type CompanyAddressProps = {
  cardIndex: string;
  accountSettings: AccountSettings;
  accountId?: string;
  api: FetchWLR3AddressSearch;
};

export const CompanyAddress = ({
  cardIndex,
  accountSettings,
  accountId,
  api,
}: CompanyAddressProps) => {
  const { watch, setValue } = useFormContext();

  const business_entity_type = watch("business_entity_type");
  const isVfDirectConsumerAcc = useIsVfDirectConsumerAccount(accountSettings);
  const isVFDirect = accountSettings?.can_access_vf_direct === "1";

  /**
   * Convert date inputs to "months at address" fields which need to be sent to DC
   */
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === `companyAddressDateFrom` && type === "change") {
        setValue(
          `company_months_at_address`,
          differenceInMonths(
            new Date(),
            new Date(value[`companyAddressDateFrom`])
          )
        );
      }
      if (name === `companyAddressDateFrom2` && type === "change") {
        setValue(
          `company_months_at_address2`,
          differenceInMonths(
            new Date(value[`companyAddressDateFrom`]),
            new Date(value[`companyAddressDateFrom2`])
          )
        );
      }
      if (name === `companyAddressDateFrom3` && type === "change") {
        setValue(
          `company_months_at_address3`,
          differenceInMonths(
            new Date(value[`companyAddressDateFrom2`]),
            new Date(value[`companyAddressDateFrom3`])
          )
        );
      }
    });
    return () => subscription.unsubscribe();
  });

  const company_months_at_address = watch("company_months_at_address");
  const company_months_at_address2 = watch("company_months_at_address2");
  const company_months_at_address3 = watch("company_months_at_address3");
  const companyAddressDateFrom = watch("companyAddressDateFrom");
  const companyAddressDateFrom2 = watch("companyAddressDateFrom2");

  const partialAddressHistory =
    company_months_at_address + company_months_at_address2;
  const totalMonthsHistory =
    company_months_at_address +
    company_months_at_address2 +
    company_months_at_address3;
  const enoughHistory = totalMonthsHistory >= 36;

  // TODO: Maybe some util function for this. Not repeated code.
  //  Refactor when there's time. This was moved from pre-existing functionality
  const showAddress2 =
    isVfDirectConsumerAcc &&
    company_months_at_address !== "" &&
    company_months_at_address < 36;
  const showAddress3 =
    isVfDirectConsumerAcc &&
    company_months_at_address2 !== "" &&
    partialAddressHistory < 36;

  return (
    <IndexedCardInfo index={cardIndex} title="Company Address">
      <Grid container spacing={2}>
        {!isVFDirect && business_entity_type === "Individual" && (
          <Grid item sm={12}>
            <StatusChip
              type="info"
              message="Enter the same address as the Personal Address for this company type."
            />
          </Grid>
        )}
        {isVfDirectConsumerAcc && (
          <Grid item sm={12}>
            <Alert
              type="info"
              message="For Sole Trader / Owner Manager we require a minimum of 3 years address history."
            />
          </Grid>
        )}
        <AddressFields prefix="company_" api={api} accountId={accountId} />
        {isVfDirectConsumerAcc && (
          <>
            <Grid item sm={6}>
              <RHFSelectField
                label="Residential Status"
                name="residential_status"
                rules={{ required: true }}
              >
                <MenuItem value="OWNER">Owner</MenuItem>
                <MenuItem value="TENANT">Tenant</MenuItem>
                <MenuItem value="PARENTS">Parents</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </RHFSelectField>
            </Grid>
            <Grid item sm={3}>
              <DatePicker
                name="companyAddressDateFrom"
                label="Date from"
                disableFuture
                rules={{
                  required: true,
                  validate: (val: Date) => {
                    if (val.getFullYear() < 1900) {
                      return "Minimum date must be greater than 01/01/1900";
                    }
                    return true;
                  },
                }}
              />
            </Grid>
          </>
        )}

        <Grid item sm>
          <RHFSwitchField
            label="Billing address same as company address"
            name="billing_address_same"
          />
        </Grid>
        {showAddress2 === true && (
          <>
            <Grid item sm={12}>
              <Typography variant="h4">Previous Company Address 1</Typography>
            </Grid>
            <AddressFields
              prefix="company_"
              suffix="2"
              api={api}
              accountId={accountId}
            />
            <Grid item sm={3}>
              <DatePicker
                name="companyAddressDateFrom2"
                label="Date from"
                rules={{
                  required: true,
                }}
                margin="none"
                maxDate={companyAddressDateFrom}
              />
            </Grid>
            <Grid item sm={3}>
              <DatePicker
                name="companyAddressDateFrom"
                label="Date to"
                disabled
                disableOpenPicker
                margin="none"
              />
            </Grid>
          </>
        )}
        {showAddress3 === true && (
          <>
            <Grid item sm={12}>
              <Typography variant="h4">Previous Company Address 2</Typography>
            </Grid>
            <AddressFields
              prefix="company_"
              suffix="3"
              api={api}
              accountId={accountId}
            />
            <Grid item sm={3}>
              <DatePicker
                name="companyAddressDateFrom3"
                label="Date from"
                rules={{
                  required: true,
                  validate: (val: any) => {
                    return enoughHistory ? true : "Must be over 3 years ago";
                  },
                }}
                maxDate={companyAddressDateFrom2}
                margin="none"
              />
            </Grid>
            <Grid item sm={3}>
              <DatePicker
                name="companyAddressDateFrom2"
                label="Date to"
                disabled
                disableOpenPicker
                margin="none"
              />
            </Grid>
          </>
        )}
      </Grid>
    </IndexedCardInfo>
  );
};
